import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Breadcrumbs,
  Link as MUILink,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  IconButton,
  TextField,
  Button,
  Chip,
  TablePagination,
} from "@mui/material";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Modal, message as AntMessage } from "antd";
import { Search } from "@mui/icons-material";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteIcon from "@mui/icons-material/Delete";
import { getSuperAdminOrders, deleteOrder } from "../redux/slices/ordersSlice";
import { CircularProgress } from "@mui/material";

function AdminOrder() {
  const dispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState("");
  
  // Get orders state from Redux
  const { 
    orders = [], 
    page: currentPage = 1, 
    totalOrders = 0, 
    limit: rowsPerPageFromStore = 10,
    loading = false 
  } = useSelector((state) => state.orders || {});
  
  // Use server page (convert to 0-based for MUI)
  const [page, setPage] = useState(currentPage - 1);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageFromStore || 10);

  // Load orders when component mounts or pagination changes
  useEffect(() => {
    dispatch(getSuperAdminOrders({ 
      page: page + 1, // Convert MUI 0-based to API 1-based
      limit: rowsPerPage 
    }));
  }, [dispatch, page, rowsPerPage]);

  // Update local page state when server page changes
  useEffect(() => {
    setPage(currentPage - 1);
  }, [currentPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    // The useEffect will trigger the API call
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    // The useEffect will trigger the API call
  };

  const handleDelete = async (id) => {
    Modal.confirm({
      title: "Are you sure you want to delete this order?",
      okText: "Yes, Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await dispatch(deleteOrder(id)).unwrap();
          // Refresh with current pagination after delete
          await dispatch(getSuperAdminOrders({ 
            page: page + 1, 
            limit: rowsPerPage 
          }));
          AntMessage.success("Order deleted successfully");
        } catch (error) {
          AntMessage.error("Failed to delete order");
        }
      },
    });
  };

  // Format orders for display
  const formattedOrders = orders?.map((order) => ({
    _id: order._id,
    orderId: order._id ? order._id.slice(-6).toUpperCase() : "-",
    date: order.createdAt ? new Date(order.createdAt).toLocaleDateString() : "-",
    buyer: order.buyer?.name || "-",
    seller: order.items?.[0]?.seller?.name || "-",
    items: order.items?.map((i) => i.name).join(", ") || "-",
    amount: order.total || 0,
    status: order.orderStatus || "Pending",
    progress: order.processFlow 
      ? `${order.processFlow.filter((step) => step.completed).length}/${order.processFlow.length}`
      : "0/0",
  })) || [];

  // Apply search filter locally
  const filteredOrders = formattedOrders.filter((order) => {
    const term = searchTerm.toLowerCase();
    return (
      (order.orderId?.toLowerCase() || "").includes(term) ||
      (order.buyer?.toLowerCase() || "").includes(term) ||
      (order.seller?.toLowerCase() || "").includes(term) ||
      (order.items?.toLowerCase() || "").includes(term) ||
      (order.status?.toLowerCase() || "").includes(term)
    );
  });

  // Show either filtered or all orders
  const displayOrders = searchTerm ? filteredOrders : formattedOrders;

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "completed":
        return "success";
      case "shipped":
        return "info";
      case "processing":
        return "warning";
      case "pending":
        return "default";
      default:
        return "default";
    }
  };

  const handleDownloadPDF = () => {
    const doc = new jsPDF();
    const title = "Orders Report";
    const currentDate = new Date().toLocaleString();

    doc.setFontSize(16);
    doc.text(title, 14, 15);
    doc.setFontSize(10);
    doc.text(`Generated on: ${currentDate}`, 14, 22);

    const tableColumn = [
      "#",
      "Order ID",
      "Date",
      "Buyer",
      "Seller",
      "Items",
      "Amount (₹)",
      "Status",
      "Progress",
    ];

    const tableRows = displayOrders.map((order, index) => [
      index + 1,
      order.orderId || "-",
      order.date || "-",
      order.buyer || "-",
      order.seller || "-",
      order.items || "-",
      order.amount ? `₹${order.amount.toLocaleString()}` : "-",
      order.status || "-",
      order.progress || "-",
    ]);

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      styles: { fontSize: 8 },
      margin: { top: 30 },
    });

    const fileName = `Orders_Report_${new Date().toISOString().slice(0, 10)}.pdf`;
    doc.save(fileName);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography
        variant="h5"
        align="center"
        gutterBottom
        sx={{
          mb: 3,
          fontFamily: "Poppins, sans-serif",
          color: "#2C3E50",
          letterSpacing: "2.5px",
          lineHeight: 1.8,
        }}
      >
        Manage Orders
      </Typography>

      <Breadcrumbs
        aria-label="breadcrumb"
        sx={{
          mb: 5,
          backgroundColor: "#fff",
          padding: "15px",
          borderRadius: "12px",
          boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
          width: "fit-content",
        }}
      >
        <MUILink
          component={Link}
          to="/admin-dashboard"
          sx={{ color: "inherit", textDecoration: "none" }}
        >
          Dashboard
        </MUILink>
        <Typography sx={{ color: "primary.main" }}>Orders</Typography>
      </Breadcrumbs>

      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search by order ID, buyer, seller, items, or status"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <IconButton>
                <Search sx={{ color: "primary.main" }} />
              </IconButton>
            ),
          }}
          sx={{
            bgcolor: "white",
            borderRadius: "10px",
            boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
          }}
        />
      </Box>

      <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 2 }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<DownloadIcon />}
          onClick={handleDownloadPDF}
          disabled={displayOrders.length === 0}
        >
          Export
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
            <TableRow>
              <TableCell><strong>#</strong></TableCell>
              <TableCell><strong>Order ID</strong></TableCell>
              <TableCell><strong>Date</strong></TableCell>
              <TableCell><strong>Buyer</strong></TableCell>
              <TableCell><strong>Seller</strong></TableCell>
              <TableCell><strong>Items</strong></TableCell>
              <TableCell><strong>Amount (₹)</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell><strong>Progress</strong></TableCell>
              <TableCell><strong>Action</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 3 }}>
                  <CircularProgress size={30} />
                </TableCell>
              </TableRow>
            ) : displayOrders.length > 0 ? (
              displayOrders.map((order, index) => (
                <TableRow key={order._id}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>{order.orderId}</TableCell>
                  <TableCell>{order.date}</TableCell>
                  <TableCell>{order.buyer}</TableCell>
                  <TableCell>{order.seller}</TableCell>
                  <TableCell>{order.items}</TableCell>
                  <TableCell>
                    {order.amount ? `₹${order.amount.toLocaleString()}` : "-"}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={order.status}
                      color={getStatusColor(order.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{order.progress}</TableCell>
                  <TableCell>
                    <IconButton onClick={() => handleDelete(order._id)}>
                      <DeleteIcon color="error" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 3 }}>
                  No orders found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        {/* FIXED: TablePagination now uses server-side total count */}
        <TablePagination
          component="div"
          count={searchTerm ? displayOrders.length : totalOrders}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          // Show correct label based on search state
          labelDisplayedRows={({ from, to, count }) => 
            searchTerm 
              ? `${from}-${to} of ${count} (filtered)` 
              : `${from}-${to} of ${count}`
          }
        />
      </TableContainer>
    </Box>
  );
}

export default AdminOrder;